import { fetchAuthSession } from 'aws-amplify/auth';
import { DivConst } from 'constants/divConst';

import { useApiState } from 'hooks/useApiState';
import { useAppState } from 'hooks/useAppState';
import { useEffect, useState } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { useForm } from 'react-hook-form';
import type { FieldErrors, UseFormRegister, UseFormSetValue } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { useAtom } from 'jotai';
import { AcctState } from 'stores/atoms/acctState';
import { submit } from 'utils/app';

/* ------------------------------------------------------------------ */
/* 型定義（hook 内部 private）                                         */
/* ------------------------------------------------------------------ */

type FormStateType = {
  userName: string;
};

export interface UseInvitationUserResult {
  register: UseFormRegister<FormStateType>;
  errors: FieldErrors<FormStateType>;
  setValue: UseFormSetValue<FormStateType>;
  processing: boolean;
  onAccept: () => Promise<void>;
}

/* ------------------------------------------------------------------ */
/* hook                                                                */
/* ------------------------------------------------------------------ */

/**
 * 招待受付ユーザー情報入力画面のカスタムフック。
 * state / 副作用 / handler を集約し、JSX からロジックを分離する。
 */
export function useInvitationUser(): UseInvitationUserResult {
  /* ----------------------------------------------------------------------- */
  /* 変数定義 */
  /* ----------------------------------------------------------------------- */
  const { showBoundary } = useErrorBoundary();
  const navigate = useNavigate();
  const { invitationCd } = useParams();

  const [acctState] = useAtom(AcctState);

  const { initializeAcctState, isSignedIn } = useAppState();
  const { postInvitationRef, postInvitationAccept } = useApiState();

  const [processing, setProcessing] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<FormStateType>();

  /* ----------------------------------------------------------------------- */
  /* ライフサイクル関数定義 */
  /* ----------------------------------------------------------------------- */
  useEffect(() => {
    (async () => {
      try {
        // 未ログインの場合は、エラー画面に遷移
        if (!(await isSignedIn())) {
          throw Error();
        }
      } catch (e) {
        showBoundary(e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- マウント時 1 回のみ実行する初期化処理
  }, []);

  /* ----------------------------------------------------------------------- */
  /* イベントハンドラ関数定義 */
  /* ----------------------------------------------------------------------- */
  /**
   * 登録ボタン押下時の処理
   */
  const onAccept = () =>
    submit(
      processing,
      setProcessing,
      handleSubmit(async (data: FormStateType) => {
        try {
          // 招待情報の取得
          const responseInvitation = await postInvitationRef({
            invitationCd: invitationCd,
          });
          // 招待情報が存在しない場合はエラー画面に遷移
          if (responseInvitation.member == null) {
            throw Error();
          }
          // 招待されたメールアドレスとログイン中のメールアドレスが異なる場合はエラー画面に遷移
          if (
            acctState.user &&
            responseInvitation.member.invitationMailAddress !== acctState.user.mailAddress
          ) {
            throw Error();
          }
          // Cognito認証情報の取得
          const session = await fetchAuthSession();
          const accessToken = session.tokens?.accessToken?.toString();
          if (!accessToken) {
            throw new Error('認証トークンが取得できませんでした。');
          }

          // 招待受付APIの呼び出し
          const response = await postInvitationAccept({
            invitationCd: invitationCd,
            user: {
              accessToken: accessToken,
              name: data.userName,
              loginRouteDiv: DivConst.items.loginRoute.mail.value,
            },
          });
          await initializeAcctState(response.companyCd);
          navigate('/');
        } catch (e) {
          showBoundary(e);
        }
      })
    );

  /* ----------------------------------------------------------------------- */
  /* 戻り値 */
  /* ----------------------------------------------------------------------- */
  return {
    register,
    errors,
    setValue,
    processing,
    onAccept,
  };
}
